import { useState, useEffect, useRef, useCallback } from 'react'

import { 
  detectProcessingCapabilities, 
  processFilesChunked, 
  processFilesDirect, 
} from '../Utils/FileProcessing'

function useFileProcessor() {
  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState({ completed: 0, total: 0, percentage: 0 })
  const [error, setError] = useState(null)
  const [capabilities] = useState(() => detectProcessingCapabilities())
  
  const workerRef = useRef(null)
  const requestIdRef = useRef(0)
  const pendingRequestsRef = useRef(new Map())

  useEffect(() => {
    if (capabilities.webWorkers && !workerRef.current) {
      try {
        // Load worker from webpack-generated location
        workerRef.current = new Worker('/packs/file_processor_worker.js')
        
        workerRef.current.onmessage = (event) => {
          const { type, id, progress: workerProgress, file, files, error: workerError } = event.data
          const request = pendingRequestsRef.current.get(id)
          
          if (!request) return
          
          switch (type) {
            case 'PROGRESS':
              setProgress(workerProgress)
              if (request.onProgress && file) {
                request.onProgress(workerProgress, file)
              }
              break
              
            case 'COMPLETE':
              pendingRequestsRef.current.delete(id)
              setIsProcessing(false)
              setProgress({ completed: 0, total: 0, percentage: 0 })
              if (request.onComplete) {
                request.onComplete(files)
              }
              break
              
            case 'ERROR':
            case 'FILE_ERROR':
              pendingRequestsRef.current.delete(id)
              setIsProcessing(false)
              setError(workerError)
              if (request.onError) {
                request.onError(workerError)
              }
              break
              
            case 'CLEANUP_COMPLETE':
              pendingRequestsRef.current.delete(id)
              break
              
            case 'CLEANUP_ERROR':
              pendingRequestsRef.current.delete(id)
              // Optionally handle cleanup error here
              break
            default:
              // Unknown message type received from worker
              break
          }
        }
        
        workerRef.current.onerror = (workerError) => {
          console.warn('Web Worker error, falling back to main thread:', workerError)
          workerRef.current = null
        }
      } catch (catchError) {
        console.warn('Failed to create Web Worker, falling back to main thread:', catchError)
        workerRef.current = null
      }
    }
    
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate()
        workerRef.current = null
      }
    }
  }, [capabilities.webWorkers])

  const processFiles = useCallback((files, onProgress, onComplete, onError) => {
    if (!files || files.length === 0) {
      if (onComplete) onComplete([])
      return
    }

    setIsProcessing(true)
    setError(null)
    setProgress({ completed: 0, total: files.length, percentage: 0 })

    const handleProgress = (progressData, file) => {
      setProgress(progressData)
      if (onProgress) onProgress(progressData, file)
    }

    const handleComplete = (processedFiles) => {
      setIsProcessing(false)
      setProgress({ completed: 0, total: 0, percentage: 0 })
      if (onComplete) onComplete(processedFiles)
    }

    const handleError = (errorMessage, fileName) => {
      setIsProcessing(false)
      setError(errorMessage)
      if (onError) onError(errorMessage, fileName)
    }

    if (workerRef.current && capabilities.webWorkers) {
      requestIdRef.current += 1
      const requestId = requestIdRef.current
      pendingRequestsRef.current.set(requestId, { onProgress, onComplete, onError })
      
      workerRef.current.postMessage({
        type: 'PROCESS_FILES',
        id: requestId,
        data: { files },
      })
    } else if (capabilities.preferredMethod === 'chunked') {
      processFilesChunked(files, handleProgress, handleComplete, handleError)
    } else {
      processFilesDirect(files, handleProgress, handleComplete, handleError)
    }
  }, [capabilities])

  return {
    processFiles,
    isProcessing,
    progress,
    error,
    capabilities,
  }
}

export default useFileProcessor
